import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Lock, CheckCircle2, Play, Clock } from 'lucide-react';

export default function CourseDetail() {
  const { user } = useAuth();
  const { slug = 'headspace' } = useParams();
  const [course, setCourse] = useState(null);
  const [completed, setCompleted] = useState([]);

  useEffect(() => {
    api.get(`/courses/${slug}`).then(r => setCourse(r.data)).catch(() => {});
    if (user) api.get('/progress').then(r => setCompleted(r.data.completed_lessons || [])).catch(() => {});
  }, [user, slug]);

  if (!course) return <div className="kc-container py-16 text-muted-kc">Loading…</div>;

  const totalDone = completed.length;
  const lockedExists = (course.lessons || []).some((l) => !l.is_free_preview);
  const userPaid = user?.has_paid;

  return (
    <div>
      {/* Header */}
      <section className="bg-ink text-white kc-noise relative">
        <div className="absolute inset-0 gold-sheen pointer-events-none" />
        <div className="kc-container relative py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            <div className="text-[11px] tracking-[0.18em] uppercase text-white/60">{course.subtitle}</div>
            <h1 className="mt-3 font-display uppercase text-5xl md:text-7xl leading-[0.95]" data-testid="course-title">{course.title}</h1>
            <p className="mt-3 text-white/80 text-base md:text-lg max-w-2xl">{course.description}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to={`/learn/${(course.lessons||[])[0]?.id}`} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="course-start"><Play size={16}/> Start Lesson 1</Link>
              {lockedExists && !userPaid && (
                <Link to="/unlock" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] border border-white/30 font-semibold hover:bg-white/10 transition-colors" data-testid="course-unlock">Unlock full course</Link>
              )}
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="rounded-[var(--kc-radius-md)] overflow-hidden border border-white/15 max-w-xs mx-auto">
              <img src={course.cover_image} alt={`${course.title} cover`} className="w-full block" />
            </div>
          </div>
        </div>
      </section>

      {/* What you'll learn */}
      <section className="kc-container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">What you'll learn</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl mt-2">{course.tagline || 'Level up.'}</h2>
          </div>
          <ul className="md:col-span-7 space-y-3">
            {(course.what_youll_learn || []).map((p, i) => (
              <li key={i} className="flex gap-3 items-start text-ink-2" data-testid={`learn-point-${i}`}>
                <CheckCircle2 className="text-gold mt-0.5 flex-shrink-0" size={20}/>
                <span className="text-base">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Curriculum */}
      <section className="bg-surface-2 py-12 md:py-16">
        <div className="kc-container">
          <div className="flex items-end justify-between gap-3 mb-6">
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Curriculum</div>
              <h2 className="font-display uppercase text-3xl md:text-4xl mt-2">{course.lesson_count} lessons</h2>
            </div>
            {user && <div className="text-sm text-muted-kc" data-testid="course-completed-count">{totalDone} completed</div>}
          </div>
          <div className="space-y-2">
            {(course.lessons || []).map((l, i) => {
              const isDone = completed.includes(l.id);
              const locked = !l.is_free_preview && !userPaid;
              return (
                <Link key={l.id} to={locked ? '/unlock' : `/learn/${l.id}`} className={`flex items-center gap-4 bg-white border ${isDone ? 'border-gold' : 'border-kc'} rounded-[var(--kc-radius-md)] p-4 hover:shadow-kc transition-shadow`} data-testid={`curr-lesson-${i}`}>
                  <span className="font-display text-2xl text-gold w-12 text-center">{String(i+1).padStart(2,'0')}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-muted-kc">{l.subtitle}</div>
                    <div className="font-semibold truncate">{l.title}</div>
                    <div className="text-xs text-muted-kc mt-0.5 flex items-center gap-1"><Clock size={11}/> {l.reading_time_min || 6} min read</div>
                  </div>
                  {isDone && <CheckCircle2 className="text-gold" size={20}/>}
                  {locked && <Lock className="text-muted-kc" size={18}/>}
                  {l.is_free_preview && !isDone && <span className="text-[10px] tracking-wider font-bold uppercase bg-gold-soft border border-gold text-ink px-2 py-1 rounded-full">Free</span>}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Author */}
      <section className="kc-container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Your instructor</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl mt-2">{course.author_name}</h2>
          </div>
          <div className="md:col-span-7 text-ink-2 leading-relaxed">
            <p>{course.author_bio}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
